import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Clock3, Download, FileText, LogOut, PenLine, RefreshCw, Send, ShieldCheck, UploadCloud, XCircle } from "lucide-react";
import { LOGO } from "../../data/content";
import { SignaturePad } from "../../components/SignaturePad";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

const STEPS = [
  { key: "personal", label: "Personal" },
  { key: "address", label: "Address" },
  { key: "emergency", label: "Emergency" },
  { key: "identity", label: "Identity & Bank" },
  { key: "documents", label: "Documents" },
  { key: "signature", label: "E-Signature" },
  { key: "review", label: "Review" },
];

const DOCUMENTS = [
  ["aadhaar_copy", "Aadhaar copy", true],
  ["pan_copy", "PAN copy", true],
  ["education_certificates", "Educational certificates", true],
  ["experience_certificates", "Experience certificates", false],
  ["address_proof", "Address proof", true],
  ["bank_proof", "Bank proof", true],
  ["photograph", "Photograph", true],
  ["driving_licence", "Driving licence", false],
];

const input = "w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors";
const label = "block text-[10px] tracking-[0.18em] uppercase text-slate-500 mb-2";
const btn = "bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3 transition-colors disabled:opacity-50";
const ghostBtn = "border border-white/15 hover:border-nx-gold/60 text-slate-300 hover:text-white text-xs tracking-[0.12em] uppercase px-4 py-2.5 transition-colors disabled:opacity-50";

const statusClass = (status) =>
  status === "verified" || status === "signed" || status === "approved"
    ? "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"
    : status === "rejected"
      ? "border-red-500/50 text-red-300 bg-red-500/10"
      : "border-amber-500/40 text-amber-300 bg-amber-500/10";

const Field = ({ name, label: text, value, onChange, type = "text", options, required, disabled, textarea }) => (
  <label className="block">
    <span className={label}>{text}{required ? " *" : ""}</span>
    {options ? (
      <select className={`${input} [&_option]:bg-white [&_option]:text-slate-900`} value={value || ""} disabled={disabled} onChange={(e) => onChange(name, e.target.value)} data-testid={`onboarding-field-${name}`}>
        <option value="">Select</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    ) : textarea ? (
      <textarea className={`${input} min-h-28`} value={value || ""} disabled={disabled} onChange={(e) => onChange(name, e.target.value)} data-testid={`onboarding-field-${name}`} />
    ) : (
      <input className={input} type={type} value={value || ""} disabled={disabled} onChange={(e) => onChange(name, e.target.value)} data-testid={`onboarding-field-${name}`} />
    )}
  </label>
);

const SectionStatus = ({ item }) => {
  const Icon = item?.state === "completed" ? CheckCircle2 : item?.state === "needs_correction" ? XCircle : Clock3;
  return <span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] tracking-[0.14em] uppercase ${statusClass(item?.status)}`}><Icon size={12} />{item?.state === "needs_correction" ? "Correction" : item?.status || "pending"}</span>;
};

const SignatureRequestCard = ({ req, onSigned }) => {
  const sigRef = useRef(null);
  const [typedName, setTypedName] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    const res = await api.get(`/career-onboarding/signature-requests/${req.id}/document`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  const sign = async () => {
    if (!typedName.trim()) return toast.error("Type your full name");
    if (!accepted) return toast.error("Please accept the declaration");
    if (!sigRef.current || sigRef.current.isEmpty()) return toast.error("Draw your signature");
    setBusy(true);
    try {
      await api.post(`/career-onboarding/signature-requests/${req.id}/sign`, {
        typed_name: typedName,
        signature_png: sigRef.current.toDataURL("image/png"),
        accepted,
        declaration_version: "v1",
      });
      toast.success("Document signed");
      onSigned();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save signature");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="border border-white/10 bg-white/[0.02] p-5" data-testid={`signature-request-${req.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-white text-sm font-medium">{req.title}</p>
          <p className="text-[11px] text-slate-500 mt-1">Requested {new Date(req.requested_at).toLocaleString("en-IN")}</p>
        </div>
        <span className={`border px-2.5 py-1 text-[10px] tracking-[0.14em] uppercase ${statusClass(req.status)}`}>{req.status}</span>
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <button type="button" onClick={download} className={ghostBtn} data-testid={`signature-download-${req.id}`}><Download size={13} className="inline mr-1" /> View Document</button>
        {req.status === "signed" && <span className="text-xs text-emerald-300 inline-flex items-center"><ShieldCheck size={13} className="mr-1" /> Signed by {req.typed_name}</span>}
      </div>
      {req.status !== "signed" && (
        <div className="mt-5 grid gap-4">
          <SignaturePad ref={sigRef} testId={`signature-pad-${req.id}`} />
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => sigRef.current?.clear()} className={ghostBtn} data-testid={`signature-clear-${req.id}`}>Clear</button>
          </div>
          <Field name={`typed-name-${req.id}`} label="Type full name" value={typedName} onChange={(_, v) => setTypedName(v)} required />
          <label className="flex items-start gap-3 text-xs text-slate-300 leading-relaxed">
            <input type="checkbox" className="mt-1 accent-[#d4af5e]" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} data-testid={`signature-accept-${req.id}`} />
            I confirm that I have read this document and agree to sign it electronically. I understand that my typed name, drawn signature, date/time and device audit details will be recorded.
          </label>
          <button type="button" onClick={sign} disabled={busy} className={btn} data-testid={`signature-submit-${req.id}`}><PenLine size={13} className="inline mr-1" /> {busy ? "Saving…" : "Sign Document"}</button>
        </div>
      )}
    </div>
  );
};

export default function OnboardingPortal() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const resubmitMode = searchParams.get("resubmit") === "1";
  const [invite, setInvite] = useState(null);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ personal: {}, address: {}, emergency: {}, identity: {} });
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const redeemStarted = useRef(false);

  const loadPortal = useCallback(async () => {
    const { data: payload } = await api.get("/career-onboarding/portal");
    setData(payload);
    setForm({
      personal: payload.data?.personal || {},
      address: payload.data?.address || {},
      emergency: payload.data?.emergency || {},
      identity: payload.data?.identity || {},
    });
    setVerified(true);
  }, []);

  const sendOtp = useCallback(async () => {
    try {
      await api.post("/career-onboarding/otp/send");
      setOtpSent(true);
      toast.success("Verification code sent to your email");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send verification code");
    }
  }, []);

  useEffect(() => {
    const redeem = async () => {
      if (!token || redeemStarted.current) return;
      redeemStarted.current = true;
      setBusy(true);
      try {
        const { data: payload } = await api.post("/career-onboarding/redeem", { token });
        setInvite(payload);
        await sendOtp();
      } catch (err) {
        const detail = err?.response?.data?.detail;
        setError(typeof detail === "string" ? detail : "This onboarding link is invalid or expired.");
      } finally {
        setBusy(false);
      }
    };
    redeem();
  }, [token, sendOtp]);

  const verifyOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/career-onboarding/otp/verify", { code: otp });
      await loadPortal();
      if (resubmitMode) setStep(STEPS.findIndex((st) => st.key === "documents"));
      toast.success("Email verified");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid verification code");
    } finally {
      setBusy(false);
    }
  };

  const updateSection = (section, name, value) => setForm((p) => ({ ...p, [section]: { ...p[section], [name]: value } }));
  const save = async (nextStep = false) => {
    setBusy(true);
    try {
      const { data: payload } = await api.put("/career-onboarding/portal", form);
      setData(payload);
      if (nextStep) setStep((s) => Math.min(s + 1, STEPS.length - 1));
      toast.success("Progress saved");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save progress");
    } finally {
      setBusy(false);
    }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data: payload } = await api.post(`/career-onboarding/documents/${kind}`, fd);
      setData(payload);
      toast.success("Document uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadDoc = async (doc) => {
    const res = await api.get(`/career-onboarding/documents/${doc.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    window.open(url, "_blank", "noopener");
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/career-onboarding/submit");
      await loadPortal();
      toast.success("Onboarding submitted for HR verification");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Please complete the pending fields");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    await api.post("/career-onboarding/logout").catch(() => {});
    setVerified(false);
    setData(null);
    setInvite(null);
    toast.success("Signed out");
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 bg-nx-navy" data-testid="onboarding-invalid">
        <div className="max-w-md border border-white/10 bg-white/[0.02] p-8 text-center">
          <img src={LOGO.fullDark} alt="NexSecure Technologies" className="h-20 mx-auto mb-6" />
          <h1 className="font-marcellus text-2xl text-white mb-3">Onboarding link unavailable</h1>
          <p className="text-sm text-slate-400">{error}</p>
          <Link to="/careers" className="inline-block mt-6 text-xs tracking-[0.14em] uppercase text-nx-goldlight hover:text-white">Back to Careers</Link>
        </div>
      </div>
    );
  }

  if (!verified) {
    return (
      <div className="min-h-screen flex items-center justify-center px-5 bg-nx-navy" data-testid="onboarding-otp-page">
        <div className="w-full max-w-md border border-white/10 bg-white/[0.02] p-8">
          <img src={LOGO.fullDark} alt="NexSecure Technologies" className="h-20 mx-auto mb-6" />
          <p className="text-[10px] tracking-[0.26em] uppercase text-slate-500 text-center">Candidate Onboarding</p>
          <h1 className="font-marcellus text-2xl text-white text-center mt-2">{invite?.candidate_name || "Secure verification"}</h1>
          <p className="text-sm text-slate-400 text-center mt-3">We sent a verification code to <span className="text-nx-goldlight">{invite?.email || "your registered email"}</span>.</p>
          <form onSubmit={verifyOtp} className="mt-8 grid gap-4">
            <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="6-digit code" className={`${input} text-center text-xl tracking-[0.4em]`} data-testid="onboarding-otp-input" />
            <button className={btn} disabled={busy || otp.length !== 6} data-testid="onboarding-otp-verify">{busy ? "Verifying…" : "Verify & Continue"}</button>
            <button type="button" onClick={sendOtp} className={ghostBtn} data-testid="onboarding-otp-resend"><Send size={13} className="inline mr-1" /> {otpSent ? "Resend Code" : "Send Code"}</button>
          </form>
        </div>
      </div>
    );
  }

  const current = STEPS[step];
  const locked = ["submitted", "approved"].includes(data?.status) && !(data?.progress?.steps || []).some((s) => s.state === "needs_correction");
  const sidebarSteps = [
    ...(data?.progress?.steps || []),
    { key: "review", label: "Review", status: data?.submitted_at ? "verified" : "pending", state: data?.submitted_at ? "completed" : "pending", comment: "" },
  ];

  return (
    <div className="min-h-screen bg-nx-navy text-slate-200" data-testid="candidate-onboarding-portal">
      <header className="border-b border-white/10 bg-nx-navy2">
        <div className="nx-wrap flex items-center justify-between h-16">
          <div className="flex items-center gap-3"><img src={LOGO.iconDark} alt="" className="h-8" /><span className="text-[10px] tracking-[0.26em] uppercase text-slate-400">Candidate Onboarding</span></div>
          <button onClick={logout} className="text-xs text-slate-400 hover:text-white" data-testid="onboarding-logout"><LogOut size={14} className="inline mr-1" /> Sign out</button>
        </div>
      </header>
      <main className="nx-wrap py-8 lg:py-12 grid lg:grid-cols-[300px_1fr] gap-8">
        <aside className="border border-white/10 bg-white/[0.02] p-5 h-fit" data-testid="onboarding-progress">
          <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500">Reference</p>
          <p className="font-mono text-nx-goldlight mt-1">{data?.reference_number}</p>
          <p className="text-white font-medium mt-4">{data?.candidate_name}</p>
          <p className="text-xs text-slate-500">{data?.candidate_email}</p>
          <div className="mt-6 h-1 bg-white/10"><div className="h-1 bg-nx-gold" style={{ width: `${((data?.progress?.completed || 0) / (data?.progress?.total || 1)) * 100}%` }} /></div>
          <div className="mt-5 grid gap-2">
            {sidebarSteps.map((s, i) => (
              <button key={s.key} onClick={() => setStep(i)} className={`flex items-center justify-between gap-3 border px-3 py-2 text-left text-xs ${current.key === s.key ? "border-nx-gold/60 text-white" : "border-white/10 text-slate-400"}`} data-testid={`progress-step-${s.key}`}>
                <span>{s.label}</span><SectionStatus item={s} />
              </button>
            ))}
          </div>
        </aside>
        <section className="border border-white/10 bg-white/[0.02] p-5 sm:p-8" data-testid={`onboarding-step-${current.key}`}>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-7">
            <div>
              <p className="text-[10px] tracking-[0.24em] uppercase text-nx-goldlight">Step {step + 1} of {STEPS.length}</p>
              <h1 className="font-marcellus text-3xl text-white mt-2">{current.label}</h1>
            </div>
            <span className={`border px-3 py-1.5 text-[10px] tracking-[0.16em] uppercase ${statusClass(data?.status)}`} data-testid="onboarding-status">{data?.status}</span>
          </div>

          {current.key === "personal" && (
            <div className="grid md:grid-cols-2 gap-5">
              <Field name="full_name" label="Full name" required value={form.personal.full_name} onChange={(n, v) => updateSection("personal", n, v)} />
              <Field name="date_of_birth" label="Date of birth" type="date" required value={form.personal.date_of_birth} onChange={(n, v) => updateSection("personal", n, v)} />
              <Field name="gender" label="Gender" options={["Male", "Female", "Other"]} required value={form.personal.gender} onChange={(n, v) => updateSection("personal", n, v)} />
              <Field name="blood_group" label="Blood group" options={["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"]} value={form.personal.blood_group} onChange={(n, v) => updateSection("personal", n, v)} />
              <Field name="marital_status" label="Marital status" options={["Single", "Married", "Other"]} value={form.personal.marital_status} onChange={(n, v) => updateSection("personal", n, v)} />
              <Field name="nationality" label="Nationality" value={form.personal.nationality} onChange={(n, v) => updateSection("personal", n, v)} />
              <Field name="personal_mobile" label="Personal mobile" required value={form.personal.personal_mobile} onChange={(n, v) => updateSection("personal", n, v)} />
              <Field name="personal_email" label="Personal email" value={form.personal.personal_email} disabled onChange={(n, v) => updateSection("personal", n, v)} />
            </div>
          )}

          {current.key === "address" && (
            <div className="grid gap-5">
              <Field name="permanent_address" label="Permanent address" textarea required value={form.address.permanent_address} onChange={(n, v) => updateSection("address", n, v)} />
              <Field name="current_address" label="Current address" textarea required value={form.address.current_address} onChange={(n, v) => updateSection("address", n, v)} />
              <label className="flex items-center gap-3 text-sm text-slate-300"><input type="checkbox" className="accent-[#d4af5e]" checked={!!form.address.same_as_permanent} onChange={(e) => updateSection("address", "same_as_permanent", e.target.checked)} data-testid="onboarding-same-address" /> Current address is same as permanent address</label>
            </div>
          )}

          {current.key === "emergency" && (
            <div className="grid md:grid-cols-2 gap-5">
              <Field name="name" label="Contact name" required value={form.emergency.name} onChange={(n, v) => updateSection("emergency", n, v)} />
              <Field name="relationship" label="Relationship" required value={form.emergency.relationship} onChange={(n, v) => updateSection("emergency", n, v)} />
              <Field name="phone" label="Phone" required value={form.emergency.phone} onChange={(n, v) => updateSection("emergency", n, v)} />
              <Field name="address" label="Address" textarea value={form.emergency.address} onChange={(n, v) => updateSection("emergency", n, v)} />
            </div>
          )}

          {current.key === "identity" && (
            <div className="grid md:grid-cols-2 gap-5">
              {data?.allow_dl_identity ? (
                <>
                  <div className="md:col-span-2 border border-nx-gold/25 bg-nx-gold/5 p-4 -mb-1">
                    <p className="text-xs text-slate-300">HR has allowed you to use your <b className="text-white">Driving Licence number</b> instead of Aadhaar + PAN.</p>
                  </div>
                  <Field name="driving_licence_number" label="Driving licence number (instead of Aadhaar/PAN)" value={form.identity.driving_licence_number} onChange={(n, v) => updateSection("identity", n, v.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 20))} />
                  <div className="hidden md:block" />
                  <Field name="aadhaar_number" label="Aadhaar number" value={form.identity.aadhaar_number} onChange={(n, v) => updateSection("identity", n, v.replace(/\D/g, "").slice(0, 12))} />
                  <Field name="pan_number" label="PAN number" value={form.identity.pan_number} onChange={(n, v) => updateSection("identity", n, v.toUpperCase().slice(0, 10))} />
                </>
              ) : (
                <>
                  <Field name="aadhaar_number" label="Aadhaar number" required value={form.identity.aadhaar_number} onChange={(n, v) => updateSection("identity", n, v.replace(/\D/g, "").slice(0, 12))} />
                  <Field name="pan_number" label="PAN number" required value={form.identity.pan_number} onChange={(n, v) => updateSection("identity", n, v.toUpperCase().slice(0, 10))} />
                </>
              )}
              <Field name="bank_name" label="Bank name" value={form.identity.bank_name} onChange={(n, v) => updateSection("identity", n, v)} />
              <Field name="bank_account_number" label="Bank account number" required value={form.identity.bank_account_number} onChange={(n, v) => updateSection("identity", n, v.replace(/\D/g, ""))} />
              <Field name="ifsc_code" label="IFSC code" required value={form.identity.ifsc_code} onChange={(n, v) => updateSection("identity", n, v.toUpperCase().slice(0, 11))} />
              <Field name="uan" label="UAN" value={form.identity.uan} onChange={(n, v) => updateSection("identity", n, v)} />
              <Field name="nominee_name" label="Nominee name" value={form.identity.nominee_name} onChange={(n, v) => updateSection("identity", n, v)} />
              <Field name="nominee_relationship" label="Nominee relationship" value={form.identity.nominee_relationship} onChange={(n, v) => updateSection("identity", n, v)} />
              <Field name="nominee_phone" label="Nominee phone" value={form.identity.nominee_phone} onChange={(n, v) => updateSection("identity", n, v)} />
              <Field name="nominee_address" label="Nominee address" textarea value={form.identity.nominee_address} onChange={(n, v) => updateSection("identity", n, v)} />
            </div>
          )}

          {current.key === "documents" && (
            <div className="grid gap-5">
              <p className="text-xs text-slate-400 border border-white/10 p-3">{data?.allow_dl_identity ? "You gave your driving licence number as ID — upload the Driving Licence copy; Aadhaar/PAN copies are then not needed. " : ""}Any document you cannot upload now will be flagged for HR to follow up; it will not block your submission.</p>
              {resubmitMode && <p className="text-xs text-amber-200 border border-amber-500/40 bg-amber-500/5 p-3" data-testid="onboarding-resubmit-note">HR has asked you to resubmit the document(s) below. Everything else you provided is already on file.</p>}
              {DOCUMENTS.filter(([kind]) => !resubmitMode || (data?.documents || []).some((d) => d.kind === kind && (d.replacement_requested_at || d.status === "rejected"))).map(([kind, text, required]) => {
                const docs = (data?.documents || []).filter((d) => d.kind === kind);
                const redo = docs.some((d) => d.replacement_requested_at || d.status === "rejected");
                const rejected = docs.find((d) => d.status === "rejected");
                const canUpload = redo || (!docs.some((d) => d.status === "verified") && data?.status !== "submitted");
                return (
                  <div key={kind} className="border border-white/10 p-4" data-testid={`document-slot-${kind}`}>
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div><p className="text-white text-sm">{text}{required ? " *" : ""}</p><p className="text-[11px] text-slate-500">PDF, JPG or PNG · up to 5 MB</p>
                        {rejected ? <p className="text-[11px] text-red-300 mt-1" data-testid={`document-rejected-${kind}`}>Rejected by HR{rejected.rejected_reason ? ` — ${rejected.rejected_reason}` : ""}. Please re-upload the correct document.</p>
                          : redo && <p className="text-[11px] text-amber-300 mt-1" data-testid={`document-replace-requested-${kind}`}>HR has requested a replacement for this document — please re-upload it.</p>}
                      </div>
                      {canUpload && <label className={ghostBtn} data-testid={`document-upload-label-${kind}`}><UploadCloud size={13} className="inline mr-1" /> {redo ? "Re-upload" : "Upload"}<input type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => upload(kind, e.target.files?.[0])} data-testid={`document-upload-${kind}`} /></label>}
                    </div>
                    {docs.length > 0 && <div className="mt-4 grid gap-2">{docs.filter((d) => d.status !== "replaced").map((d) => (
                      <div key={d.id} className="flex flex-wrap items-center gap-3 border-t border-white/5 pt-3" data-testid={`candidate-document-${d.id}`}>
                        <FileText size={15} className="text-nx-goldlight" /><button type="button" onClick={() => downloadDoc(d)} className="text-xs text-slate-200 hover:text-nx-goldlight">{d.filename}</button>
                        <span className={`ml-auto border px-2 py-1 text-[10px] uppercase ${statusClass(d.status)}`}>{d.status}</span>
                        {d.comment && <span className="w-full text-xs text-red-300">HR note: {d.comment}</span>}
                      </div>
                    ))}</div>}
                  </div>
                );
              })}
            </div>
          )}

          {current.key === "signature" && (
            <div className="grid gap-5">
              {(data?.signature_requests || []).length === 0 ? (
                <p className="text-sm text-slate-400" data-testid="signature-empty">No e-signature request is pending right now. HR can send one after reviewing your documents.</p>
              ) : data.signature_requests.map((r) => <SignatureRequestCard key={r.id} req={r} onSigned={loadPortal} />)}
            </div>
          )}

          {current.key === "review" && (
            <div className="grid gap-5" data-testid="onboarding-review">
              {(data?.progress?.steps || []).map((s) => (
                <div key={s.key} className="flex flex-wrap items-center justify-between gap-3 border border-white/10 p-4">
                  <div><p className="text-white text-sm">{s.label}</p>{s.comment && <p className="text-xs text-red-300 mt-1">HR note: {s.comment}</p>}</div>
                  <SectionStatus item={s} />
                </div>
              ))}
              {locked ? (
                <div className="border border-emerald-500/30 bg-emerald-500/10 p-5 text-emerald-200 text-sm" data-testid="onboarding-submitted-message">
                  Your onboarding has been {data?.status === "approved" ? "approved" : "submitted"}. HR will contact you if anything else is needed.
                </div>
              ) : (
                <button onClick={submit} disabled={busy} className={btn} data-testid="onboarding-submit"><CheckCircle2 size={14} className="inline mr-1" /> {busy ? "Submitting…" : "Submit for HR Verification"}</button>
              )}
            </div>
          )}

          {!["documents", "signature", "review"].includes(current.key) && (
            <div className="flex flex-wrap justify-between gap-3 mt-8 pt-6 border-t border-white/10">
              <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} className={ghostBtn} data-testid="onboarding-back">Back</button>
              <div className="flex gap-2">
                <button type="button" onClick={() => save(false)} disabled={busy} className={ghostBtn} data-testid="onboarding-save">{busy ? "Saving…" : "Save Progress"}</button>
                <button type="button" onClick={() => save(true)} disabled={busy} className={btn} data-testid="onboarding-next">Save & Continue</button>
              </div>
            </div>
          )}
          {["documents", "signature"].includes(current.key) && (
            <div className="flex justify-between mt-8 pt-6 border-t border-white/10">
              <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} className={ghostBtn} data-testid="onboarding-back">Back</button>
              <button type="button" onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} className={btn} data-testid="onboarding-next">Continue</button>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
